"use client";

import { useEffect } from "react";

const API_KEY = "1a5fa0a4164a2eaf39f12db5738a34b6";

/**
 * Amplitude autocapture + session replay.
 *
 * The Amplitude SDK + session-replay plugin are ~85 KB gzipped — far too heavy
 * to sit in the critical First Load JS of every page. So we load them with a
 * **dynamic import deferred to browser idle** (after hydration), which moves the
 * whole library into its own async chunk that never blocks initial render or
 * interactivity. Autocapture (pageViews, sessions, web-vitals, …) still records
 * the entry page because `init` captures the current view; only the very first
 * event is delayed by a beat, which is the standard analytics tradeoff.
 */
export default function Amplitude() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      Promise.all([
        import("@amplitude/analytics-browser"),
        import("@amplitude/plugin-session-replay-browser"),
      ])
        .then(([amplitude, { sessionReplayPlugin }]) => {
          if (cancelled) return;
          amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));
          amplitude.init(API_KEY, undefined, {
            fetchRemoteConfig: true,
            autocapture: {
              attribution: true,
              fileDownloads: true,
              formInteractions: true,
              pageViews: true,
              sessions: true,
              elementInteractions: true,
              networkTracking: true,
              webVitals: true,
              frustrationInteractions: {
                thrashedCursor: true,
                errorClicks: true,
                deadClicks: true,
                rageClicks: true,
              },
            },
          });
        })
        .catch(() => {
          /* analytics is best-effort; never surface a load failure */
        });
    };

    // Wait for idle so analytics never competes with the initial render/LCP.
    const supportsIdle = typeof window.requestIdleCallback === "function";
    const handle: number = supportsIdle
      ? window.requestIdleCallback(start, { timeout: 4000 })
      : window.setTimeout(start, 2000);

    return () => {
      cancelled = true;
      if (supportsIdle && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle);
      }
    };
  }, []);

  return null;
}
